import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { Language } from '@adornis/internationalization/language-iso639-2.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { normalizeEmail } from '../api/email-filter.js';
import { SmartTeamsLocation } from './SmartTeamsLocation.js';

type MongoBeforeCreateArgs = Parameters<typeof MongoEntity.beforeCreate>;
type MongoTransformBeforeUpdateArgs = Parameters<typeof MongoEntity.transformBeforeUpdate>;
type MongoAfterUpdateArgs = Parameters<typeof MongoEntity.afterUpdate>;

@Entity()
export class SmartTeamsFieldEngineer extends MongoEntity {
  static override _class = 'SmartTeamsFieldEngineer';
  static override _collectionName = 'smartteams_field_engineers';

  @validate(nonOptional())
  @Field(() => [String])
  partnerCompanyIds: Maybe<string[]>;

  @validate(nonOptional())
  @Field(() => String)
  name: Maybe<string>;

  @Field(() => String)
  email: Maybe<string>;

  @validate(({ value, key, path }) => {
    if (!value) return;
    const pattern = /^[A-Z]{2}::[A-Za-z0-9]+::NSC[A-Za-z0-9]+$/;
    if (!pattern.test(value)) throw new ValidationError('ID pattern does not match', { key, path });
  })
  @Field(() => String)
  externalId: Maybe<string>;

  @Field(() => String)
  userId: Maybe<string>;

  @Field(() => SmartTeamsLocation)
  defaultLocation: Maybe<SmartTeamsLocation>;

  @Field(() => Language)
  firstLanguage: Maybe<keyof typeof Language>;

  @Field(() => Language)
  secondLanguage: Maybe<keyof typeof Language>;

  @Field(() => Boolean, { default: v => v ?? false })
  archived: Maybe<boolean>;

  // Soft-delete marker. Null/absent = live; a Date = deleted (hidden from all lists and pickers).
  // Setting it also frees the email for reuse — the partial unique index on `email` keys on
  // `deletedAt: null` (see entrypoints/smartteams/src/server/migrations/ensure-user-email-indexes.ts).
  @Field(() => Date)
  deletedAt: Maybe<Date>;

  @Field(() => Date, { default: v => v ?? new Date() })
  createdAt: Maybe<Date>;

  @Field(() => Boolean, { default: v => v ?? false })
  mustChangePassword: Maybe<boolean>;

  // Timestamp of the most recent successful onboarding email. Null/absent means no onboarding
  // email has ever been delivered for this field engineer. Refreshed on every successful send
  // (re-send, CSV import, create-with-user), so it always reflects the latest delivery.
  @Field(() => Date)
  onboardingEmailSentAt: Maybe<Date>;

  @Field(() => String)
  serviceNowSysId: Maybe<string>;

  /**
   * True for an engineer who works for a partner company; false or absent means NSC-internal. Filled from the
   * `Engineer Type` column of the field engineer import (`partner`; blank or `internal` = internal) and
   * editable on the engineer form, because ServiceNow cannot tell the two apart reliably.
   */
  @Field(() => Boolean, { default: v => v ?? false })
  isPartner: Maybe<boolean>;

  // Canonicalize the email to lowercase on every write, so callers never have to remember to.
  // Uniqueness is enforced by the partial unique index on `email`
  // (see entrypoints/smartteams/src/server/migrations/ensure-user-email-indexes.ts), which is the
  // race-safe source of truth; friendly duplicate messages come from the create mutations/importer.
  static override async beforeCreate(...args: MongoBeforeCreateArgs) {
    const [entity] = args;
    const fe = entity as SmartTeamsFieldEngineer;
    if (fe.email) fe.email = normalizeEmail(fe.email);
    return super.beforeCreate(...args);
  }

  static override async transformBeforeUpdate(...args: MongoTransformBeforeUpdateArgs) {
    const [, update] = args;
    const fe = update as SmartTeamsFieldEngineer;
    if (fe.email) fe.email = normalizeEmail(fe.email);
    return super.transformBeforeUpdate(...args);
  }

  static override async afterUpdate(...args: MongoAfterUpdateArgs) {
    const [oldEntry, newEntry] = args;
    // The framework hands this hook only the fields the form changed (e.g. just `partnerCompanyIds`), not
    // the whole record, so the rest is read from the pre-update document.
    const changed = Object.fromEntries(
      Object.entries((newEntry as SmartTeamsFieldEngineer).toJSON() as Record<string, unknown>).filter(
        ([, value]) => value !== undefined,
      ),
    ) as Partial<SmartTeamsFieldEngineer>;
    const fe = { ...(oldEntry as Partial<SmartTeamsFieldEngineer>), ...changed };
    // Sync the login record when the update carried the identity (as before) or changed the company. The
    // company case used to be skipped, so removing an engineer from their company left the login record
    // pointing at it — and anything keyed on the company (the service desk email) kept applying.
    const syncs = (changed.userId && changed.email) || changed.partnerCompanyIds !== undefined;
    if (syncs && fe.userId && fe.email) {
      const { ensureAppUserForFieldEngineer } = await import('../api/create-app-user-for-fe.js');
      await ensureAppUserForFieldEngineer({
        userId: fe.userId,
        email: fe.email,
        primaryLanguage: fe.firstLanguage ?? undefined,
        serviceNowFEId: fe.serviceNowSysId ?? undefined,
        // Follow the engineer's company, including removal: an empty list clears it on the login record.
        companyId: Array.isArray(fe.partnerCompanyIds) ? (fe.partnerCompanyIds[0] ?? null) : undefined,
      });
    }
    return super.afterUpdate(...args);
  }
}
